import { isEmpty } from 'lodash-es';
import { IJob, IJobCandidateInteraction, ILocation } from 'wisestep-api';
import { IFilterList } from 'wisestep-base';

import JobsCrmPipelineAdvanceFiltersFormProps from '../../../../components/filters/models/JobsCrmPipelineAdvanceFiltersFormProps';
import JobsCrmSmartSourceAdvanceFiltersFormProps from '../../../../components/filters/models/JobsCrmSmartSourceAdvanceFiltersFormProps';
import { IBreadCrumbsList } from '../../../../components/WsBreadCrumbs';
import { IResultSetInfo } from '../../../../features/emailSettings/hooks/useGetJobCampaignAnalytics';
import { jobCrmStatesCount } from '../../../jobs/listing/JObSearchFilterDefaultQuery';
import { JobsCrmDefaultRequest } from '../requests/JobsCrmDefaultRequest';

export enum JobsCrm {
  SMART_SOURCE = 'smartSource',
  INSIGHTS = 'insights',
  PIPELINE = 'pipeline',
  TASKS = 'tasks',
  SIMILAR_JOBS = 'similarJobs',
  JOB_ANALYTICS = 'jobAnalytics',
}
export const smartSourceTabsList = [
  {
    tabName: 'AI Suggestion',
    tabCount: 0,
  },
];
export const pipelineTabsList = (stats?: jobCrmStatesCount) => {
  return [
    {
      tabName: 'Mapping' + `(${stats?.mappingsByJob ?? 0})`,
      tabCount: 0,
    },
    {
      tabName: 'Submission' + `(${stats?.submissionsByJob ?? 0})`,
      tabCount: 0,
    },
    {
      tabName: 'Applications' + `(${stats?.applicationsByJob ?? 0})`,
      tabCount: 0,
    },
    {
      tabName: 'Uploaded' + `(${stats?.uploadedByJob ?? 0})`,
      tabCount: 0,
    },
  ];
};
export const pushPipelineFilters = (data: JobsCrmPipelineAdvanceFiltersFormProps) => {
  const filtersArray: IFilterList[] = [];
  if (data.commPreferences?.length)
    filtersArray.push({
      tag: `Comm. Pref.: ${data.commPreferences.join(',')}`,
      id: 'commPreferences',
    });
  if (data.contactAvailability?.length)
    filtersArray.push({
      tag: `Contact Availability: ${data.contactAvailability.join(',')}`,
      id: 'contactAvailability',
    });
  if (data.locations?.length)
    filtersArray.push({
      tag: `Locations.: ${data.locations
        .map((location) => `${location.city}-${location.state}-${location.country}`)
        .join(',')}`,
      id: 'locations',
    });
  if (data.noticePeriod && data.noticePeriod.min)
    filtersArray.push({
      tag: `Notice Period: ${data.noticePeriod.min}-${data.noticePeriod?.max}`,
      id: 'noticePeriod',
    });
  if (!isEmpty(data.relocationPreferences))
    filtersArray.push({
      tag: `Relocation Pref.: ${data.relocationPreferences}`,
      id: 'relocationPreferences',
    });
  if (!isEmpty(data.currentStageCurrentStatusPairs)) {
    const stageStstusObj = JSON.parse(data.currentStageCurrentStatusPairs ?? '');
    filtersArray.push({
      tag: `Business Definition: ${stageStstusObj.businessDefinitionName}`,
      id: 'businessDefinition',
    });
  }

  if (data.source?.length)
    filtersArray.push({
      tag: `Source(s): ${data.source?.join(',')}`,
      id: 'source',
    });
  if (data.tags?.length)
    filtersArray.push({
      tag: `Tag(s): ${data.tags.join(',')}`,
      id: 'tags',
    });
  if (data.workAuthorization?.length)
    filtersArray.push({
      tag: `Work Auth.: ${data.workAuthorization.join(',')}`,
      id: 'workAuthorization',
    });
  if (data.salaryRange?.currency)
    filtersArray.push({
      tag: `${data.salaryRange.min ?? ''} to ${data.salaryRange.max ?? ''} ${
        data.salaryRange.currency ?? ''
      } ${data.salaryRange.unitTime ?? ''}`,
      id: 'salaryRange',
    });
  if (data.createdBy?.length)
    filtersArray.push({
      tag: `Created By: ${data.createdBy.join(',')}`,
      id: 'createdBy',
    });
  return filtersArray;
};

const recencyMap = new Map();
recencyMap.set('0,90', '0-3 Months');
recencyMap.set('90,180', '3-6 Months');
recencyMap.set('180,360', '6-12 Months');
recencyMap.set('360,9999', '> 12 Months');

export const pushSmartSourceFilters = (
  data: JobsCrmSmartSourceAdvanceFiltersFormProps
): IFilterList[] => {
  const filtersArray: IFilterList[] = [];

  if (data.aiLocations)
    filtersArray.push({ tag: `AI Locations: ${data.aiLocations}`, id: 'aiLocations' });
  if (data.contactAvailability?.length)
    filtersArray.push({
      tag: `Contact Availability: ${data.contactAvailability}`,
      id: 'contactAvailability',
    });
  if (data.customLocations?.length)
    filtersArray.push({
      tag: `${data.customLocations
        .map((location) => `${location.city}-${location.state}-${location.country}`)
        .join(',')}`,
      id: 'customLocations',
    });
  return filtersArray;
};

export const getDefaultCandidateSearchType = (index: number) => {
  switch (index) {
    case 0:
    case 2:
    case 3:
      return 'OnlyMappings';
    case 1:
      return 'OnlySubmissions';
    default:
      return 'All';
  }
};

export const getDefaultCandidateType = (index: number) => {
  switch (index) {
    case 0:
      return 'mappings';
    case 1:
      return 'submissions';
    case 2:
      return 'Applications';
    case 3:
      return 'uploaded';
    default:
      return 'mappings';
  }
};

export const getDeafaultCandidateSearchTypeList = (index: number) => {
  switch (index) {
    case 0:
      return ['mapped'];
    case 1:
      return ['submitted'];
    case 2:
      return ['Applied'];
    case 3:
      return ['uploaded'];
    default:
      return ['mapped', 'submitted', 'uploaded', 'applied'];
  }
};

export const jobsCrmTabNames = [
  {
    tabName: 'Pipelines',
  },
  {
    tabName: 'Similar Jobs',
  },
  {
    tabName: 'AI Smart Source',
  },
  {
    tabName: 'Analytics Insights',
  },
  {
    tabName: 'Reminders',
  },
  {
    tabName: 'Job Analytics',
  },
];

export type JobsCrmRouteOptions =
  | 'pipeline'
  | 'similarJobs'
  | 'smartSource'
  | 'insights'
  | 'tasks'
  | 'jobAnalytics';
export const jobsCrmRouteNames: JobsCrmRouteOptions[] = [
  'pipeline',
  'similarJobs',
  'smartSource',
  'insights',
  'tasks',
  'jobAnalytics',
];
export const getDefaultAdavnacedSetFilters = (
  data: IJobCandidateInteraction,
  job: IJob
): JobsCrmSmartSourceAdvanceFiltersFormProps => {
  const defaultValues: JobsCrmSmartSourceAdvanceFiltersFormProps =
    {} as JobsCrmSmartSourceAdvanceFiltersFormProps;
  defaultValues.relevance = [data.minMatchScore ?? 90, 100];
  defaultValues.contactAvailability = [];
  if (data.phoneAvailability) {
    defaultValues.contactAvailability.push('Phone');
  }
  if (data.emailAvailability) {
    defaultValues.contactAvailability.push('Email');
  }
  if (data?.candidateAllLocationFields?.length) {
    if (areFieldValuesEqual(job.locations ?? [], data.candidateAllLocationFields, 'city')) {
      defaultValues.aiLocations = 'Same City as Job';
    } else if (areFieldValuesEqual(job.locations ?? [], data.candidateAllLocationFields, 'state')) {
      defaultValues.aiLocations = 'Same State as Job';
    } else if (
      areFieldValuesEqual(job.locations ?? [], data.candidateAllLocationFields, 'country')
    ) {
      defaultValues.aiLocations = 'Same Country as Job';
    } else {
      defaultValues.aiLocations = 'custom';
      defaultValues.customLocations = data.candidateAllLocationFields;
    }
  }

  return defaultValues;
};

export const generateJobCrmBackwardURL = (label): IBreadCrumbsList[] => [
  { label: 'Jobs', url: `/jobs` },
  { label: label, url: `${window.location.pathname}${window.location.search}` },
];

export const locationDisplay = (job: IJob) =>
  job.locations?.length ? formatLocation(job?.locations?.[0]) : 'Not Available';

export const formatLocation = ({ city, state, country }: ILocation = {} as ILocation) =>
  [city, state, country].filter(Boolean).join(', ') || 'Not Available';

const areFieldValuesEqual = <T>(listA: T[], ListB: T[], matchField: keyof T) => {
  if (listA.length !== ListB.length) return false;
  return listA.every((obj, index) => {
    const compare = ListB[index];
    return obj[matchField] === compare[matchField];
  });
};

export const getDeliveryStats = (resultSet: IResultSetInfo[] = []) => {
  const stats = {
    DELIVERED: {
      label: 'Delivered',
      value: 0,
      campaignCode: '',
    },
    BOUNCED: {
      label: 'Not Delivered',
      value: 0,
      campaignCode: '',
    },
    OPENED: {
      label: 'Read',
      value: 0,
      campaignCode: '',
    },
    SENT: {
      label: 'Sent',
      value: 0,
      campaignCode: '',
    },
  };

  for (const { delivery_status, count, campaign_code } of resultSet) {
    if (delivery_status in stats) {
      stats[delivery_status].value = count;
      stats[delivery_status].campaignCode = campaign_code;
    }
  }

  return stats;
};

export const defaultAISmartSourcePayload = (job: IJob): IJobCandidateInteraction => {
  const defaultRequest = { ...JobsCrmDefaultRequest };
  defaultRequest.jobIds = [job.id ?? ''];
  defaultRequest.jobCode = job.jobCode ?? '';
  defaultRequest.source = 'AI_Suggestions_new';
  defaultRequest.jobMatchingContent = [job?.title ?? '', job?.description ?? ''];
  defaultRequest.excludeDuplicateRecords = true;
  defaultRequest.jobTitle = job.title;
  defaultRequest.candidateSearchType = 'All';
  defaultRequest.emailAvailability = true;
  defaultRequest.candidateAllLocationFields = (job?.locations ?? []).map((location) => ({
    state: location.state,
    country: location.country,
    continent: '',
    continentCode: '',
    countryCode: '',
    stateCode: '',
    city: '',
  }));
  defaultRequest.skills = job.skills;
  defaultRequest.phoneAvailability = true;
  defaultRequest.minMatchScore = 90;
  defaultRequest.size = 20;
  defaultRequest.query = '';
  return defaultRequest;
};
